/**
 * 登录态（会话）。
 *
 * 为什么是模块级单例而不是 `provide/inject`：
 * `AppHeader` 与登录页是**兄弟**关系，而不是父子；额度条又嵌在头部里。
 * 用 provide/inject 就得让每个入口自己记得 provide 一次，漏一处就是"头部显示未登录、
 * 页面显示已登录"。模块单例没有这个失配面。
 *
 * `hydrated` 是本文件最重要的字段：它为 false 时，任何"未登录 → 跳登录页"的判断
 * 都必须让路。否则首屏会先用空状态判定一次，把已登录用户闪一下登录页。
 *
 * 这里只做"拿到结果 → 写状态"，不做路由跳转：跳转属于视图层的决定。
 */

import { reactive } from 'vue';

import {
  AuthResultSchema,
  ErrorCode,
  MeResponseSchema,
  PasswordLoginResponseSchema,
  PasswordLoginSchema,
  SendEmailCodeResponseSchema,
  SendSmsResponseSchema,
  type AuthResult,
  type MeResponse,
  type Profile,
  type Quota,
  type SendEmailCodeResponse,
  type SendSmsResponse,
  type User,
  type UserRole,
} from '@spirit/contracts';

import { ApiError, apiRequest, isAbortError } from './api-client';
import { getDeviceId } from './device';
import { IgnoredDataSchema } from './schemas';
import { clearSubscription, loadSubscription } from './subscription-store';
import { clearSession, getRefreshToken, hydrateTokens, setSession } from './token-store';

/** 认证状态机的两个稳定状态；中间的"还在恢复"用 `hydrated:false` 表达。 */
export type AuthStatus = 'anonymous' | 'authenticated';

export interface AuthState {
  status: AuthStatus;
  user: User | null;
  /**
   * 当前身份角色，`null` 表示未登录。
   *
   * `user.role` 已经是契约 `UserSchema` 的必填字段，这里再存一份是**派生缓存**而不是新真源：
   * 路由守卫、顶部导航、登录后的落地页都要判它，而"从 `user?.role` 里挖"的兜底写法
   * 每处都得自己处理 `user === null`——漏一处就是越权入口（未登录却看得见后台导航）。
   * 集中成 `role` + `isStaffRole()` 之后，判据只有一个地方可以写错。
   */
  role: UserRole | null;
  profile: Profile | null;
  quota: Quota | null;
  /**
   * 恢复流程是否**已经结束**（成功、失败、无令牌都算结束）。
   *
   * 它不是"正在恢复"：为 `false` 时既可能是"还没开始"，也可能是"请求在飞"。
   * **渲染**判断（首屏骨架屏、"未登录才显示登录表单"）读它；
   * 而**流程**判断（"等恢复完了再决定去哪"，也就是路由守卫）必须 `await whenAuthReady()`——
   * 只读这个标志拿到的是一份"还没恢复完"的空状态，看到 `anonymous` 就会把已登录用户
   * 判成未登录（task-8 的 ROOT 深链被弹去登录页就是这么来的）。
   */
  hydrated: boolean;
  /**
   * 恢复请求是否在飞。
   *
   * 与 `hydrated` 的差别落在"还没开始"那个窗口：`hydrated === false && bootstrapping === false`
   * 说明恢复流程压根没启动（例如守卫比 `main.ts` 里那句不 await 的 `bootstrapAuth()` 更早跑）。
   * 想区分"正在恢复"与"已恢复但未登录（可点重试）"的 UI 需要它，`hydrated` 单独用做不到。
   */
  bootstrapping: boolean;
  /** 是否有认证相关请求在飞（按钮禁用用）。 */
  pending: boolean;
  error: string | null;
}

const INITIAL_STATE: AuthState = {
  status: 'anonymous',
  user: null,
  role: null,
  profile: null,
  quota: null,
  hydrated: false,
  bootstrapping: false,
  pending: false,
  error: null,
};

const state = reactive<AuthState>({ ...INITIAL_STATE });

/** 当前状态（只读语义，不要直接改；改状态请用本模块导出的函数）。 */
export function getAuthState(): AuthState {
  return state;
}

/**
 * 仅供测试：把会话状态机复位到初始值。
 *
 * 连单飞 Promise 一起清 —— 模块级单例的用例之间会互相串状态，
 * 只清字段不清 Promise 的话，第二个用例会拿到上一个用例已兑现的恢复流程，
 * 于是"冷启动"根本不会发生（而冷启动正是这些用例要验的东西）。
 */
export function resetAuthStoreForTest(): void {
  Object.assign(state, INITIAL_STATE);
  bootstrapPromise = null;
}

/**
 * 是否"后台身份"（管理员 / ROOT）。
 *
 * 只在这里判一次：导航渲染（切后台导航）、路由守卫（普通用户一律 404）、
 * 登录后的落地页三处都依赖它。散开写成 `role === 'admin'` 的话，
 * 任何一处漏掉 `'root'` 都会变成"ROOT 反而进不去后台"的隐蔽缺陷。
 */
export function isStaffRole(role: UserRole | null): boolean {
  return role === 'admin' || role === 'root';
}

/** 当前角色；未登录为 `null`。 */
export function currentRole(): UserRole | null {
  return state.role;
}

/** 置为未登录并带上原因（`null` 表示"本来就没登录"，不是错误）。 */
export function markAnonymous(message: string | null = null): void {
  state.status = 'anonymous';
  state.user = null;
  state.role = null;
  state.profile = null;
  state.quota = null;
  state.error = message;
  // 订阅跟着会话走：留着上一个账号的档位与到期时间，是跨账号的信息泄漏。
  // 这里是**唯一**的"变成未登录"的收口（40100 广播、登出、冷启动无令牌都经过它），
  // 所以清理只写这一处就够了。
  clearSubscription();
}

/** 服务端返回了新的额度快照时调用（登录、问答结算、手动刷新）。 */
export function applyQuota(quota: Quota): void {
  state.quota = quota;
}

/** 资料更新后调用。 */
export function applyProfile(profile: Profile | null): void {
  state.profile = profile;
}

/**
 * 拉取当前用户（含额度快照）。
 *
 * 只在**已经有令牌**时调用；它不做任何"要不要登录"的判断，判断留给调用方。
 */
export function loadMe(): Promise<MeResponse> {
  return apiRequest('/auth/me', MeResponseSchema, { method: 'GET' });
}

let bootstrapPromise: Promise<void> | null = null;

/**
 * 恢复登录态。
 *
 * 幂等：多个组件在挂载时同时调用，只会真正跑一次。失败时把
 * `bootstrapPromise` 复位成 null，这样用户可以点"重试"再来一次——
 * 一个永久缓存的失败 Promise 会让重试按钮变成装饰品。
 */
export function bootstrapAuth(): Promise<void> {
  if (bootstrapPromise === null) {
    bootstrapPromise = runBootstrap().catch((error: unknown) => {
      bootstrapPromise = null;
      console.warn('[auth-store] 登录态恢复失败', error);
    });
  }
  return bootstrapPromise;
}

/**
 * 等待"恢复登录态"这一步结束（成功、失败、无令牌都算结束）。
 *
 * 守卫为什么必须用它而不是读 `hydrated`：`hydrated` 只是一个**快照**，读到 `false`
 * 仅仅说明"还没结束"，从读到它到真正结束之间没有任何同步点——守卫拿着这份空状态
 * 就会把已登录的 ROOT 判成未登录。这里返回的是**同一个单飞 Promise**，所以并发导航
 * 只会打一次 `/auth/me`；恢复已经结束时立即兑现，不重打网络（这也是不写成无条件
 * `bootstrapAuth()` 的原因：失败后它会把单飞 Promise 复位，无条件等等于每次导航都重试）。
 */
export function whenAuthReady(): Promise<void> {
  if (state.hydrated) {
    return Promise.resolve();
  }
  return bootstrapAuth();
}

async function runBootstrap(): Promise<void> {
  // `bootstrapping` 必须在**任何 await 之前**置位：守卫可能刚刚看到
  // `hydrated === false && bootstrapping === false`，这一行之后它就变成"在飞"了。
  state.bootstrapping = true;
  try {
    hydrateTokens();

    if (getRefreshToken() === null) {
      markAnonymous(null);
      return;
    }

    try {
      const result = await loadMe();
      state.status = 'authenticated';
      state.user = result.user;
      state.role = result.user.role;
      state.profile = result.profile;
      state.quota = result.quota;
      state.error = null;
      // 档位徽章与到期横幅要用到订阅；`/auth/me` 不带它，所以并行补一枪。
      // 不 await：订阅拉不到不该拖住"登录态已恢复"这件事（徽章会晚一点出现）。
      void loadSubscription();
    } catch (error) {
      // 只有"令牌确实无效"才降级为未登录。网络错误时保持现状，
      // 否则后端重启一次，所有在线用户都会被登出。
      if (
        error instanceof ApiError &&
        (error.code === ErrorCode.UNAUTHORIZED ||
          error.code === ErrorCode.TOKEN_EXPIRED ||
          error.code === ErrorCode.REFRESH_INVALID)
      ) {
        markAnonymous(error.message);
        return;
      }
      state.error = describeAuthError(error);
    }
  } finally {
    // 结束标志必须在**每一条出口**上置位（含上面的 `return`）：它既是骨架屏的开关，
    // 也是 `whenAuthReady()` 的判据。漏一条出口，骨架屏永远不消失、守卫永远等下去。
    state.bootstrapping = false;
    state.hydrated = true;
  }
}

/** 发送登录验证码。冷却由登录页自己管，这里不掺和 UI 计时。 */
export function sendSmsCode(phone: string): Promise<SendSmsResponse> {
  return apiRequest('/auth/sms/send', SendSmsResponseSchema, {
    method: 'POST',
    body: { phone, scene: 'login' },
    auth: false,
  });
}

/**
 * 校验验证码并登录。
 *
 * `deviceId` 走**请求体**（契约 §3.1），不是请求头——服务端不读任何设备头。
 * 登录成功后立刻写入令牌，再顺手拉一次 `/auth/me`；`/auth/me` 失败不影响登录，
 * 只是额度条晚一点出现（下一次问答的 `usage` 帧会补上）。
 */
export async function verifySmsCode(phone: string, code: string): Promise<AuthResult> {
  state.pending = true;
  state.error = null;
  try {
    const result = await apiRequest('/auth/sms/verify', AuthResultSchema, {
      method: 'POST',
      body: { phone, code, deviceId: getDeviceId() },
      auth: false,
    });

    setSession(result);
    state.status = 'authenticated';
    state.user = result.user;
    state.role = result.user.role;
    state.profile = result.profile;
    state.error = null;

    try {
      const me = await loadMe();
      state.user = me.user;
      state.role = me.user.role;
      state.profile = me.profile;
      state.quota = me.quota;
      void loadSubscription();
    } catch (error) {
      console.warn('[auth-store] 登录成功但 /auth/me 拉取失败，额度条将稍后由其它请求补上', error);
    }

    return result;
  } finally {
    state.pending = false;
  }
}

/** 发送邮箱登录验证码。冷却同样由登录页自己管（与短信通道共用一套计时机制）。 */
export function sendEmailCode(email: string): Promise<SendEmailCodeResponse> {
  return apiRequest('/auth/email/send', SendEmailCodeResponseSchema, {
    method: 'POST',
    body: { email, scene: 'login' },
    auth: false,
  });
}

/**
 * 校验邮箱验证码并登录（未注册的邮箱由服务端直接建号）。
 *
 * 成功后的写入路径与 `verifySmsCode` **逐句相同**（落令牌 → 写状态机 → 补一发 `/auth/me`）。
 * 没有把它和短信版合并成一个"传 identifier"的通用函数：两条通道的请求字段名不同
 * （`phone` / `email`），且契约里是两个独立的 schema；合并只会把字段名藏进一个参数里，
 * 将来某一条通道单独加字段时还得再拆开。
 */
export async function verifyEmailCode(email: string, code: string): Promise<AuthResult> {
  state.pending = true;
  state.error = null;
  try {
    const result = await apiRequest('/auth/email/verify', AuthResultSchema, {
      method: 'POST',
      body: { email, code, deviceId: getDeviceId() },
      auth: false,
    });

    setSession(result);
    state.status = 'authenticated';
    state.user = result.user;
    state.role = result.user.role;
    state.profile = result.profile;
    state.error = null;

    try {
      const me = await loadMe();
      state.user = me.user;
      state.role = me.user.role;
      state.profile = me.profile;
      state.quota = me.quota;
      void loadSubscription();
    } catch (error) {
      console.warn('[auth-store] 登录成功但 /auth/me 拉取失败，额度条将稍后由其它请求补上', error);
    }

    return result;
  } finally {
    state.pending = false;
  }
}

/**
 * 清掉当前错误。
 *
 * 视图拿到的是 `useAuth()` 的只读 getter，改不了 `state.error`；而"换了个 tab，
 * 上一条通道的报错还挂在表单下面"会让用户以为新通道也失败了。
 */
export function clearAuthError(): void {
  state.error = null;
}

/** 普通用户被账号密码通道拒掉时的文案（服务端 40100 也是同一句话术）。 */
const STAFF_ONLY_MESSAGE = '该账号没有后台权限';

/**
 * 账号密码登录（管理员 / ROOT 专用）。
 *
 * 与 `verifySmsCode` 是**同一个登录页上的另一种识别方式**：请求体形状不同，成功后的
 * 会话写入路径完全一致（`setSession` → 状态机 → 补一发 `/auth/me`）。
 *
 * 三个刻意的决定：
 * 1. **发送前先过契约**。`PasswordLoginSchema` 里的 `UsernameSchema` 会把 `Admin` 归一成
 *    `admin` 再发出去；前端不做这一步，用户"明明输对了账号"却会拿到 40100。
 *    校验失败也**不浪费一次必然失败的往返**。
 * 2. **普通用户主动清会话并抛错**（纵深防御）。服务端已经以 40100 拒绝 `role === 'user'`，
 *    这里再拦一次的理由是：前端不能把"进不进后台"的最终判据交给一个随时可能改口径的响应。
 *    一旦真发生，必须把刚写进存储的令牌清掉——"令牌已落盘但状态是未登录"的半登录状态
 *    比登录失败难排查得多。
 * 3. **不 `loadSubscription()`**。契约 §3.6 明确管理员 / ROOT 没有个人额度与订阅，
 *    补那一枪只会得到必然无用的请求；后台外壳本身也不渲染额度条。
 */
export async function passwordLogin(username: string, password: string): Promise<void> {
  state.pending = true;
  state.error = null;
  try {
    const payload = PasswordLoginSchema.safeParse({
      username,
      password,
      deviceId: getDeviceId(),
    });
    if (!payload.success) {
      throw new ApiError({
        code: ErrorCode.INVALID_PARAM,
        message: payload.error.issues[0]?.message ?? '账号或密码格式不正确',
        httpStatus: 400,
        requestId: '',
        details: undefined,
      });
    }

    const result = await apiRequest('/auth/password/login', PasswordLoginResponseSchema, {
      method: 'POST',
      body: payload.data,
      auth: false,
    });

    if (!isStaffRole(result.user.role)) {
      clearSession();
      markAnonymous(STAFF_ONLY_MESSAGE);
      throw new ApiError({
        code: ErrorCode.UNAUTHORIZED,
        message: STAFF_ONLY_MESSAGE,
        httpStatus: 401,
        requestId: '',
        details: undefined,
      });
    }

    setSession(result);
    state.status = 'authenticated';
    state.user = result.user;
    state.role = result.user.role;
    state.profile = result.profile;
    state.error = null;

    try {
      // `/auth/me` 里的 role 是权威值（服务端刚算过的），登录响应里的只是回显。
      const me = await loadMe();
      state.user = me.user;
      state.role = me.user.role;
      state.profile = me.profile;
      state.quota = me.quota;
    } catch (error) {
      console.warn('[auth-store] 账号密码登录成功但 /auth/me 拉取失败', error);
    }
  } finally {
    state.pending = false;
  }
}

/** 登出。无论服务端是否成功，本地一律清干净。 */
export async function signOut(): Promise<void> {
  state.pending = true;
  try {
    const refreshToken = getRefreshToken();
    await apiRequest('/auth/logout', IgnoredDataSchema, {
      method: 'POST',
      ...(refreshToken === null ? {} : { body: { refreshToken } }),
    });
  } catch (error) {
    // 登出接口失败不阻塞用户：本地会话必须清掉，否则用户会卡在
    // "点了登出但还是登录状态"的荒谬处境里。
    console.warn('[auth-store] 登出接口调用失败，已在本地清除会话', error);
  } finally {
    clearSession();
    state.pending = false;
    markAnonymous(null);
    state.hydrated = true;
  }
}

/** 把异常翻译成能直接显示的文案。 */
export function describeAuthError(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (isAbortError(error)) {
    return '请求已取消';
  }
  return '网络异常，请检查网络后重试';
}

/** 组合式入口：模板里直接 `const auth = useAuth()`。 */
export interface AuthApi extends AuthState {
  isAuthenticated: boolean;
}

export function useAuth(): AuthApi {
  return {
    get status() {
      return state.status;
    },
    get user() {
      return state.user;
    },
    get role() {
      return state.role;
    },
    get profile() {
      return state.profile;
    },
    get quota() {
      return state.quota;
    },
    get hydrated() {
      return state.hydrated;
    },
    get bootstrapping() {
      return state.bootstrapping;
    },
    get pending() {
      return state.pending;
    },
    get error() {
      return state.error;
    },
    get isAuthenticated() {
      return state.status === 'authenticated';
    },
  };
}
